type DatePickerKind = "date" | "datetime-local";

interface DatePickerValue {
    date: Date;
    time: string;
}

let openDatePicker: (() => void) | null = null;
let nextDatePickerId = 0;

function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function parseDate(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) {
        return null;
    }

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return formatDate(date) === `${match[1]}-${match[2]}-${match[3]}` ? date : null;
}

function parseValue(value: string): DatePickerValue | null {
    const date = parseDate(value);
    if (!date) {
        return null;
    }

    const timeMatch = /T(\d{2}):(\d{2})/.exec(value);
    return {
        date,
        time: timeMatch ? `${timeMatch[1]}:${timeMatch[2]}` : "09:00"
    };
}

function createButton(text: string, ariaLabel: string, className?: string): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = text;
    button.setAttribute("aria-label", ariaLabel);
    if (className) {
        button.className = className;
    }
    return button;
}

function dispatchValueChange(input: HTMLInputElement): void {
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
}

function datePart(value: string): string {
    return value.slice(0, 10);
}

function enhanceDateInput(input: HTMLInputElement): void {
    if (input.dataset.cmDatePickerEnhanced === "true" || input.dataset.cmDatePicker === "false") {
        return;
    }

    const originalType = input.getAttribute("type")?.toLowerCase();
    if (originalType !== "date" && originalType !== "datetime-local") {
        return;
    }

    const kind = originalType as DatePickerKind;
    input.dataset.cmDatePickerEnhanced = "true";
    input.dataset.cmDatePickerKind = kind;
    input.type = "text";
    input.readOnly = true;
    input.autocomplete = "off";
    input.classList.add("cm-date-input");

    const wrapper = document.createElement("span");
    wrapper.className = "cm-date-control";
    input.parentNode?.insertBefore(wrapper, input);
    wrapper.appendChild(input);

    const trigger = createButton("▦", `Open ${kind === "date" ? "date" : "date and time"} picker`, "cm-date-picker-trigger");
    trigger.disabled = input.disabled;
    trigger.setAttribute("aria-haspopup", "dialog");
    wrapper.appendChild(trigger);

    const popup = document.createElement("div");
    popup.id = input.id ? `${input.id}CmDatePicker` : `cmDatePicker${++nextDatePickerId}`;
    popup.className = `cm-date-picker${kind === "datetime-local" ? " cm-date-picker--with-time" : ""}`;
    popup.hidden = true;
    popup.setAttribute("role", "dialog");
    popup.setAttribute("aria-modal", "false");
    popup.setAttribute("aria-label", kind === "date" ? "Choose date" : "Choose date and time");
    // A modal makes content outside itself inert, including a body-level calendar.
    const hostDialog = input.closest("dialog");
    (hostDialog ?? document.body).appendChild(popup);

    input.setAttribute("aria-haspopup", "dialog");
    input.setAttribute("aria-controls", popup.id);
    input.setAttribute("aria-expanded", "false");
    trigger.setAttribute("aria-controls", popup.id);
    trigger.setAttribute("aria-expanded", "false");

    let parsed = parseValue(input.value);
    let viewDate = parsed?.date ?? new Date();
    let pendingDate = parsed?.date ?? null;
    let pendingTime = parsed?.time ?? "09:00";

    const close = (restoreFocus = false): void => {
        if (popup.hidden) {
            return;
        }

        popup.hidden = true;
        input.setAttribute("aria-expanded", "false");
        trigger.setAttribute("aria-expanded", "false");
        if (openDatePicker === close) {
            openDatePicker = null;
        }
        if (restoreFocus) {
            input.focus();
        }
    };

    const positionPopup = (): void => {
        if (popup.hidden) {
            return;
        }

        const controlRect = wrapper.getBoundingClientRect();
        const viewportGap = 8;
        const popupWidth = Math.min(320, window.innerWidth - viewportGap * 2);
        popup.style.width = `${popupWidth}px`;

        const popupHeight = popup.offsetHeight;
        const left = Math.max(viewportGap, Math.min(controlRect.left, window.innerWidth - popupWidth - viewportGap));
        const roomBelow = window.innerHeight - controlRect.bottom - viewportGap;
        const top = roomBelow >= popupHeight
            ? controlRect.bottom + 5
            : Math.max(viewportGap, controlRect.top - popupHeight - 5);
        popup.style.left = `${Math.round(left)}px`;
        popup.style.top = `${Math.round(top)}px`;
    };

    const commitDate = (date: Date | null, time = pendingTime): void => {
        input.value = date
            ? kind === "datetime-local" ? `${formatDate(date)}T${time}` : formatDate(date)
            : "";
        dispatchValueChange(input);
    };

    const render = (): void => {
        popup.replaceChildren();

        const header = document.createElement("div");
        header.className = "cm-date-picker__header";
        const previous = createButton("‹", "Previous month", "cm-date-picker__nav");
        const title = document.createElement("span");
        title.className = "cm-date-picker__title";
        title.textContent = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(viewDate);
        const next = createButton("›", "Next month", "cm-date-picker__nav");
        previous.addEventListener("click", () => {
            viewDate = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1);
            render();
        });
        next.addEventListener("click", () => {
            viewDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
            render();
        });
        header.append(previous, title, next);

        const weekdays = document.createElement("div");
        weekdays.className = "cm-date-picker__weekdays";
        for (const weekday of ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]) {
            const label = document.createElement("span");
            label.textContent = weekday;
            weekdays.appendChild(label);
        }

        const days = document.createElement("div");
        days.className = "cm-date-picker__days";
        const firstOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
        const firstVisible = new Date(firstOfMonth);
        firstVisible.setDate(firstVisible.getDate() - firstOfMonth.getDay());
        const selectedValue = pendingDate ? formatDate(pendingDate) : "";
        const todayValue = formatDate(new Date());
        const minValue = datePart(input.getAttribute("min") ?? "");
        const maxValue = datePart(input.getAttribute("max") ?? "");

        for (let offset = 0; offset < 42; offset++) {
            const date = new Date(firstVisible);
            date.setDate(firstVisible.getDate() + offset);
            const value = formatDate(date);
            const day = createButton(
                date.getDate().toString(),
                new Intl.DateTimeFormat(undefined, { dateStyle: "full" }).format(date),
                "cm-date-picker__day");
            const outsideRange = (minValue !== "" && value < minValue) || (maxValue !== "" && value > maxValue);
            day.classList.toggle("is-outside", date.getMonth() !== viewDate.getMonth());
            day.classList.toggle("is-today", value === todayValue);
            day.classList.toggle("is-selected", value === selectedValue);
            day.setAttribute("aria-pressed", (value === selectedValue).toString());
            day.disabled = outsideRange;
            day.addEventListener("click", () => {
                pendingDate = date;
                if (kind === "date") {
                    commitDate(date);
                    close(true);
                    return;
                }
                render();
            });
            days.appendChild(day);
        }

        popup.append(header, weekdays, days);

        if (kind === "datetime-local") {
            const timeRow = document.createElement("label");
            timeRow.className = "cm-date-picker__time";
            const timeLabel = document.createElement("span");
            timeLabel.textContent = "Time";
            const timeInput = document.createElement("input");
            timeInput.type = "time";
            timeInput.value = pendingTime;
            timeInput.setAttribute("aria-label", "Time");
            timeInput.addEventListener("input", () => {
                pendingTime = timeInput.value || "00:00";
            });
            timeRow.append(timeLabel, timeInput);
            popup.appendChild(timeRow);
        }

        const footer = document.createElement("div");
        footer.className = "cm-date-picker__footer";
        const today = createButton("Today", "Select today");
        const footerEnd = document.createElement("span");
        footerEnd.className = "cm-date-picker__footer-end";
        const clear = createButton("Clear", "Clear date");
        today.addEventListener("click", () => {
            const now = new Date();
            pendingDate = now;
            viewDate = now;
            if (kind === "date") {
                commitDate(now);
                close(true);
            } else {
                pendingTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
                render();
            }
        });
        clear.addEventListener("click", () => {
            pendingDate = null;
            commitDate(null);
            close(true);
        });
        footerEnd.appendChild(clear);
        if (kind === "datetime-local") {
            const done = createButton("Done", "Apply date and time", "cm-date-picker__done");
            done.disabled = !pendingDate;
            done.addEventListener("click", () => {
                if (pendingDate) {
                    commitDate(pendingDate, pendingTime);
                    close(true);
                }
            });
            footerEnd.appendChild(done);
        }
        footer.append(today, footerEnd);
        popup.appendChild(footer);
        positionPopup();
    };

    const open = (): void => {
        if (input.disabled) {
            return;
        }

        openDatePicker?.();
        openDatePicker = close;
        parsed = parseValue(input.value);
        pendingDate = parsed?.date ?? null;
        pendingTime = parsed?.time ?? "09:00";
        viewDate = pendingDate ?? new Date();
        popup.hidden = false;
        input.setAttribute("aria-expanded", "true");
        trigger.setAttribute("aria-expanded", "true");
        render();
    };

    input.addEventListener("click", open);
    trigger.addEventListener("click", open);
    input.addEventListener("keydown", event => {
        if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
            event.preventDefault();
            open();
        } else if (event.key === "Escape") {
            close();
        }
    });
    popup.addEventListener("click", event => event.stopPropagation());
    popup.addEventListener("keydown", event => {
        if (event.key === "Escape") {
            event.preventDefault();
            close(true);
        }
    });
    document.addEventListener("click", event => {
        const target = event.target as Node;
        if (!popup.hidden && !wrapper.contains(target) && !popup.contains(target)) {
            close();
        }
    });
    hostDialog?.addEventListener("close", () => close());
    window.addEventListener("resize", positionPopup);
    window.addEventListener("scroll", positionPopup, true);

    new MutationObserver(() => {
        trigger.disabled = input.disabled;
    }).observe(input, { attributes: true, attributeFilter: ["disabled"] });
}

function enhanceWithin(root: ParentNode): void {
    if (root instanceof HTMLInputElement) {
        enhanceDateInput(root);
    }
    root.querySelectorAll<HTMLInputElement>('input[type="date"], input[type="datetime-local"]').forEach(enhanceDateInput);
}

function initializeDatePickers(): void {
    enhanceWithin(document);
    new MutationObserver(records => {
        for (const record of records) {
            for (const node of record.addedNodes) {
                if (node instanceof HTMLElement) {
                    enhanceWithin(node);
                }
            }
        }
    }).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeDatePickers, { once: true });
} else {
    initializeDatePickers();
}
